import type { GroupBy } from '@/serverSideTable/serverSideTable.constants';
import type { AnyProperty, ConditionalExtend } from '@/utilities.types';
import type { AdvancedFilterModel, AgGridReactProps, FilterModel, GridApi, SortModelItem } from '@seeqdev/ag/grid';
import type { IconDefinition } from '@seeqdev/icons';

export interface SubMenuItem {
  iconClass?: IconDefinition;
  iconExtraClassNames?: string;
  translationKey: string;
  action?: () => void;
  disabled?: boolean;
  tooltip?: string;
}

export interface ColumnAction<ActionKeyType> extends SubMenuItem {
  actionKey: ActionKeyType;
  subMenuItems?: SubMenuItem[];
}

export interface AgGridAsyncProps extends Omit<AgGridReactProps, 'modules'> {
  modules: AgGridModules[];
  isServerSideRowModel?: boolean;
}

interface OffsetPaginationParams {
  offset: number;
  limit: number;
}

interface CursorPaginationParams {
  previousCursorKey: string | undefined;
  limit: number;
}

interface BaseFetchParams {
  api: GridApi;
  filterModel: FilterModel | AdvancedFilterModel;
  sortModel: SortModelItem[];
  groupBy?: GroupBy;
  cancellationGroup?: string;
}

type FetchServerSideItemsParams<IsOffsetPagination extends boolean> = ConditionalExtend<
  BaseFetchParams,
  OffsetPaginationParams,
  CursorPaginationParams,
  IsOffsetPagination
>;

export type FetchServerSideItemsCallback<
  Item,
  IsOffsetPagination extends boolean = true,
  ExtraProperties = AnyProperty,
> = (
  props: FetchServerSideItemsParams<IsOffsetPagination> & ExtraProperties,
) => Promise<{ items: Item[]; totalResults?: number; cursorKey?: string }>;

export enum AgGridModules {
  ClipboardModule,
  CellSelectionModule,
  CellApiModule,
  SetFilterModule,
  ColumnMenuModule,
  ContextMenuModule,
  ColumnsToolPanelModule,
  MultiFilterModule,
  PinnedRowModule,
  RowAutoHeightModule,
  RowStyleModule,
  PaginationModule,
  RowDragModule,
  RowNumbersModule,
  RowSelectionModule,
  RowGroupingModule,
  RowApiModule,
  TextFilterModule,
  DateFilterModule,
  IntegratedChartsModule,
  TooltipModule,
  ServerSideRowModelApiModule,
  ColumnApiModule,
  QuickFilterModule,
  NumberFilterModule,
  CustomFilterModule,
  PivotModule,
  RowGroupingPanelModule,
  RenderApiModule,
  ClientSideRowModelApiModule,
  ColumnAutoSizeModule,
  CellStyleModule,
  CellSpanModule,
  EventApiModule,
  CustomEditorModule,
  TextEditorModule,
  LocaleModule,
  ScrollApiModule,
  GridStateModule,
  InfiniteRowModelModule,
  GroupFilterModule,
  ExcelExportModule,
  MasterDetailModule,
}
